import { Menu, MenuItem } from '@mui/material';
import DashboardEditMenuCategory from 'components/DashboardEditMenu/DashboardEditMenuCategory';
import { DashboardActionType } from 'components/DashboardEditMenu/helpers/actions';
import WidgetMenuItems from 'components/Widget/WidgetMenuItems';
import React from 'react';

const isMenuItem = (
    element: React.ReactNode,
): element is React.ReactElement<React.ComponentProps<typeof MenuItem>> =>
    React.isValidElement(element) && element.type === MenuItem;

const isMenu = (
    element: React.ReactNode,
): element is React.ReactElement<React.ComponentProps<typeof Menu>> =>
    React.isValidElement(element) && element.type === Menu;

const isDashboardEditMenuCategory = (
    element: React.ReactNode,
): element is React.ReactElement<
    React.ComponentProps<typeof DashboardEditMenuCategory>
> =>
    React.isValidElement(element) && element.type === DashboardEditMenuCategory;

const isWidgetMenuItem = (
    element: React.ReactNode,
): element is React.ReactElement<
    React.ComponentProps<typeof WidgetMenuItems>
> => React.isValidElement(element) && element.type === WidgetMenuItems;

/**
 * This enhancer aims to define a default close function to the IconMenu children.
 * @param children
 * @param close
 * @returns
 */
export const getEnhancedChildren = (
    children:
        | Array<React.ReactNode>
        | React.ReactElement<React.ComponentProps<typeof MenuItem>>,
    close: () => void,
) =>
    React.Children.map(children, (child) => {
        if (isMenuItem(child)) {
            React.Children.map(child, (menuItemChild) => {
                if (isMenu(menuItemChild)) {
                    return React.cloneElement(menuItemChild, {
                        onClose: (
                            event: React.MouseEvent<HTMLLIElement, MouseEvent>,
                            reason: 'backdropClick' | 'escapeKeyDown',
                        ) => {
                            if (menuItemChild.props.onClose) {
                                menuItemChild.props.onClose(event, reason);
                            }
                            close();
                        },
                    });
                }

                return menuItemChild;
            });
            return React.cloneElement(child, {
                onClick: (
                    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
                ) => {
                    if (child.props.onClick) {
                        child.props.onClick(event);
                    }
                    close();
                },
            });
        }

        if (isDashboardEditMenuCategory(child)) {
            return React.cloneElement(child, {
                onClickModal: (action: DashboardActionType) => () => {
                    if (child.props.onClickModal) {
                        child.props.onClickModal(action)();
                    }
                    close();
                },
            });
        }

        if (isWidgetMenuItem(child)) {
            return React.cloneElement(child, {
                closeWidgetMenu: () => {
                    if (child.props.closeWidgetMenu) {
                        child.props.closeWidgetMenu();
                    }
                    close();
                },
            });
        }

        return child;
    });
